import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  ProfileValueOptionResponse,
  useFetchUserProfileInfoQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import { Box, Button, Typography, Theme, SxProps, CircularProgress } from '@mui/material';

import { ProfileEditSection } from './ProfileEditSection';
import { ProfileFieldConfig, ProfilePageConfig, ProfileSectionConfig } from './types/profile.types';

const STRINGS = appStrings.modules.profile.edit;

const Actions = ({
  viewProfile,
  onSave,
  isLoading,
}: {
  viewProfile: () => void;
  onSave: () => void;
  isLoading: boolean;
}) => (
  <Box sx={actionsContainerSx}>
    <Button hierarchy="Secondary gray" size="md" onClick={viewProfile}>
      {STRINGS.viewProfile}
    </Button>
    <Button hierarchy="Primary" size="md" onClick={onSave}>
      {isLoading ? <CircularProgress sx={loadingContainerSx} size={20} /> : STRINGS.save}
    </Button>
  </Box>
);

export type ProfileTabSectionProps = Readonly<{
  pageConfig: ProfilePageConfig | ProfilePageConfig[];
  fields: ProfileFieldConfig[];
  profileOptions: ProfileValueOptionResponse;
}>;

export const ProfileTabSection = ({
  pageConfig,
  fields,
  profileOptions,
}: ProfileTabSectionProps) => {
  const { user } = useAppSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { data: userProfile } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);

  const configs = Array.isArray(pageConfig) ? pageConfig : [pageConfig];
  const firstConfig = configs[0];

  const handleSave = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  const handleViewProfile = () => {
    navigate(`${APP_ROUTE.PROFILE}/${user?.id}`);
  };

  // Helper function to get dynamic description
  const getDynamicDescription = (section: ProfileSectionConfig) => {
    // Use dynamicDescription function if available
    if (section.dynamicDescription && userProfile) {
      return section.dynamicDescription(userProfile, profileOptions);
    }

    // Fall back to static description
    return section.description ?? '';
  };

  const getDynamicTitle = (section: ProfileSectionConfig): string => {
    if (section.dynamicTitle && userProfile) {
      return section.dynamicTitle(userProfile, profileOptions);
    }

    return section.title;
  };

  return (
    <Box sx={profileTabSectionContainerSx}>
      <Box sx={contentContainerSx}>
        <Box sx={headerSx}>
          <Box sx={textContainerSx}>
            <Typography
              variant="text-lg-semibold"
              color={(theme) => theme.backrs.colors['Gray/900']}
            >
              {firstConfig.title}
            </Typography>
            <Typography variant="text-sm-regular" sx={sectionDescriptionSx}>
              {firstConfig.description}
            </Typography>
          </Box>

          <Box sx={headerActionsContainerSx}>
            <Actions viewProfile={handleViewProfile} onSave={handleSave} isLoading={isLoading} />
          </Box>
        </Box>

        {configs.map((config, index) => (
          <Box key={index}>
            {index > 0 && (
              <Box sx={headerSx}>
                <Box sx={textContainerSx}>
                  <Typography
                    variant="text-lg-semibold"
                    color={(theme) => theme.backrs.colors['Gray/900']}
                  >
                    {config.title}
                  </Typography>
                  <Typography
                    variant="text-sm-regular"
                    color={(theme) => theme.backrs.colors['Gray/600']}
                  >
                    {config.description}
                  </Typography>
                </Box>
              </Box>
            )}

            {config.sections.map((section, sectionIndex) => {
              const fieldConfigs = fields.filter((f) => section.keys.includes(f.key));

              if (fieldConfigs.length === 0) return null;

              return (
                <Box key={section.keys.join('-')} sx={sectionContainerSx}>
                  <ProfileEditSection
                    key={section.keys.join('-') + sectionIndex}
                    title={getDynamicTitle(section)}
                    titleFontVariant={section.titleFontVariant}
                    description={getDynamicDescription(section)}
                    infoModal={section.infoModal}
                    isPrivateField={section.isPrivateField ?? false}
                    fields={fieldConfigs}
                    profileOptions={profileOptions}
                    viewExamples={section.viewExamples}
                    hideDivider={section.hideDivider ?? false}
                  />
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Box sx={footerActionsContainerSx}>
        <Actions viewProfile={handleViewProfile} onSave={handleSave} isLoading={isLoading} />
      </Box>
    </Box>
  );
};

const profileTabSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  marginBottom: backrs.spacing['spacing-md'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  columnGap: backrs.spacing['spacing-xl'],
});

const textContainerSx: SxProps<Theme> = ({ backrs }) => ({
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xxs'],
});

const actionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-lg'],
  minWidth: 'max-content',
});

const sectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingY: backrs.radius['radius-lg'],
});

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: backrs.spacing['spacing-3xl'],
  height: backrs.spacing['spacing-2xl'],
  color: backrs.colors['Colors/Base/White'],
});

const headerActionsContainerSx: SxProps<Theme> = {
  display: { xs: 'none', sm: 'block' },
};

const footerActionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: { xs: 'fixed', sm: 'static' },
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: backrs.colors['Colors/Base/White'],
  padding: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
  borderTop: { xs: `1px solid ${backrs.colors['Gray/200']}`, sm: 'none' },
  zIndex: 10,
  '& .MuiButton-root:first-of-type': {
    display: {
      xs: 'none',
      sm: 'inline-flex',
    },
  },
});

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: { sm: `1px solid ${backrs.colors['Gray/200']}` },
  paddingBottom: backrs.spacing['spacing-2xl'],
  marginBottom: { xs: backrs.spacing['spacing-4xl'], sm: backrs.spacing['spacing-none'] },
});

const sectionDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
  whiteSpace: 'pre-line',
});
